'use client'

/* ---------------------------------
DoubleCard
--------------------------------- */

import React, { CSSProperties } from 'react'
import { IllustratedCard, Section } from '@dynamo-caffeina-dev/airc-ui-kit'
import { DoubleCardExtractedContentType } from '@/types'
import DOMPurify from 'isomorphic-dompurify'
import { useRouter } from 'next/navigation'
import { dispatchGTMEvent, GTM_EVENT_CATEGORY } from '@/helpers/gtm'

export default function DoubleCard({
  modelledContent: extractedContent,
}: {
  modelledContent: DoubleCardExtractedContentType
}): React.JSX.Element {
  const router = useRouter()

  return (
    <Section className={'section double-card'}>
      <div className='double-card__grid'>
        {extractedContent?.map(
          ({ chip, title, paragraph, cta, image }, index) => (
            <IllustratedCard
              key={index}
              chip={chip?.content}
              // TODO this is a workaround
              //  We get HTML while we expect plain text;
              //  data should be fixed at the CMS level.
              title={DOMPurify.sanitize(title?.content, {
                ALLOWED_TAGS: [],
              })}
              cta={cta}
              clickable
              animate={false}
              onClick={() => {
                if (cta?.href) {
                  // Special case for Amazon vendor URLs
                  if (/https?:\/\/(www\.)?amazon\.[a-z.]+/i.test(cta.href)) {
                    dispatchGTMEvent(GTM_EVENT_CATEGORY.VENDOR_CLICK, {
                      section: '', // We are allowed to send it empty if no clear section can be identified
                    })
                  }

                  // Special case for Place Locator,
                  // looks for a URL with the #place-locator anchor
                  else if (
                    /https?:\/\/[^\s#]+#place-locator(\?.*)?$/.test(cta.href)
                  ) {
                    dispatchGTMEvent(GTM_EVENT_CATEGORY.DISTRIBUTION)
                  }

                  if (
                    cta.href.startsWith('http') ||
                    cta.href.startsWith('//')
                  ) {
                    if (cta.target === '_blank') {
                      const temporaryLink = document.createElement('a')

                      temporaryLink.href = cta.href
                      temporaryLink.target = cta.target
                      temporaryLink.rel = cta.rel ?? 'noopener noreferrer'

                      document.body.appendChild(temporaryLink)

                      temporaryLink.click()
                      document.body.removeChild(temporaryLink)
                    } else {
                      window.location.href = cta.href
                    }
                  } else {
                    router.push(cta.href)
                  }
                }
              }}
              image={image?.image?.url}
              // TODO (BE)
              // we need an API field to identify the card type
              {...(index === 1
                ? {
                    style: {
                      '--airc-ui__colors-accent-600': '#1490b4',
                      '--airc-ui__colors-accent-500': '#00acde',
                      '--airc-ui__colors-accent-400': '#18b0dc',
                    } as CSSProperties,
                  }
                : {})}
            >
              <div
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(paragraph?.content ?? ''),
                }}
              />
            </IllustratedCard>
          )
        )}
      </div>
    </Section>
  )
}
